import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { F1065SchCService } from './f1065-sch-c.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { buildHeaderDataShell, normalizeFilterParams, rowPayloadFromActiveScreen } from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1065-sch-c',
    templateUrl: './f1065-sch-c.component.html',
    styleUrls: ['./f1065-sch-c.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1065SchCComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  // Flags to prevent duplicate initialization (SuperPrompt V9)
  private isInitialized = false;
  private isLoading = false;

  // STANDARD COMPONENT PROPERTIES (SuperPrompt V9)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component properties
  modalTitle = "Schedule C- Additional Information for Schedule M-3 Filers";
  title = "";
  scenarioLabel: string = "";
  entityList: any[] = [];
  scheduleFFormView: any = { rows: [] };
  formListOriginal: any = { rows: [] };
  activeTab = 1;
  tabs: any[] = [];

  // Data properties
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  baseURLs: any = {};
  processName: string = '1065 Textual Information'; // Default fallback
  
  // Entity data
  leId: any;
  cdr_no: any;
  leType: any;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private f1065SchCService: F1065SchCService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.tabs = [
      { id: 1, name: "Form Entry", active: true }
    ];
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    this.baseURLs = this.GENERAL_CONFIG;
    this.processName = this.globalService?.processName || '1065 Textual Information';
    this.scenarioLabel = this.filterParams?.scenarioLabel || this.filterParams?.scenario_label || this.globalService?.globalParams?.scenario_desc || '';
  }

  // CRITICAL: Gate function to prevent duplicate initialization (SuperPrompt V9)
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if ALL required data is present before initializing
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();
        this.loadFormData();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private setupEntityData() {
    // CRITICAL: Use REAL data from rowData (SuperPrompt V9)
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // MANDATORY ORDER: Tax Year, LEID, LE Name (SuperPrompt V9)
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];

    // Set title with scenario label from headerData
    this.title = this.modalTitle + ' ( Tax Year : ' + this.rowData.TAX_YEAR + ' - Scenario : ' + this.scenarioLabel + ' )';
  }

  loadFormData() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;

    const params = {
      action_id: 31259,
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.rowData.SCENARIO_KEY,
      jcd_key: this.rowData.JCD_KEY,
      combination_key: this.rowData.COMBINATION_KEY,
      schedule_name: "C"
    };

    this.f1065SchCService.loadPivotFormJson(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.irsForm && response.irsForm.formItemList) {
          this.processFormData(response.irsForm.formItemList);
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading form data:', error);
        this._snackBar.open('Error loading form data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  processFormData(formItemList: any[]) {
    this.scheduleFFormView.rows = [];

    for (let i = 0; i < formItemList.length; i++) {
      const formItem = formItemList[i];
      const dataRow = formItem.dataRowList[0];
      const dataCellList = dataRow.dataCellList;
      const saveParams = dataRow.rowDimensions.object_id.split('~');

      let tempDataObj: any = {
        saveParams: saveParams,
        line_order: saveParams[8]
      };

      for (let j = 0; j < dataCellList.length; j++) {
        if (j === 0) {
          tempDataObj.num = dataCellList[j].name;
        } else if (j === 1) {
          tempDataObj.description = dataCellList[j].name;
        } else if (j === 2) {
          // value1 (Yes checkbox)
          tempDataObj.lineAttrKeyA = dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name_a = dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_a = dataCellList[j].key.split('~')[2];
          tempDataObj.is_editable = dataCellList[j].property.is_editable_cell === 'Y' ? false : true;

          // Boolean transformation (SuperPrompt V9)
          if (dataCellList[j].value == null) {
            tempDataObj.value1 = "";
          } else if (dataCellList[j].value === "true") {
            tempDataObj.value1 = true;
          } else if (dataCellList[j].value === "false") {
            tempDataObj.value1 = false;
          } else {
            tempDataObj.value1 = dataCellList[j].value;
          }
        } else {
          // value2 (No checkbox)
          tempDataObj.lineAttrKeyB = dataCellList[j].key.split('~')[1];
          tempDataObj.attrib_name_b = dataCellList[j].key.split('~')[0];
          tempDataObj.attrib_order_b = dataCellList[j].key.split('~')[2];

          // Boolean transformation (SuperPrompt V9)
          if (dataCellList[j].value == null) {
            tempDataObj.value2 = "";
          } else if (dataCellList[j].value === "true") {
            tempDataObj.value2 = true;
          } else if (dataCellList[j].value === "false") {
            tempDataObj.value2 = false;
          } else {
            tempDataObj.value2 = dataCellList[j].value;
          }
        }
      }

      this.scheduleFFormView.rows.push(tempDataObj);
    }

    // Set readonly fields (SuperPrompt V9 - PUŁAPKA #4)
    if (this.scheduleFFormView.rows.length > 1) {
      this.scheduleFFormView.rows[0].value1 = this.rowData.LE_NAME;
      this.scheduleFFormView.rows[1].value1 = this.rowData.EIN;
    }

    // Backup for reset
    this.formListOriginal = JSON.parse(JSON.stringify(this.scheduleFFormView));
  }

  // CRITICAL: Mutual exclusive checkbox handler (SuperPrompt V9 - PUŁAPKA #1)
  unselectOther(index: number, type: string) {
    if (type === 'YES') {
      this.scheduleFFormView.rows[index].value2 = false;
    } else {
      this.scheduleFFormView.rows[index].value1 = false;
    }
    this.cdr.detectChanges();
  }

  save() {
    // Prevent double-click (SuperPrompt V9)
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Build save data
    const tempArray: any[] = [];

    for (let i = 0; i < this.scheduleFFormView.rows.length; i++) {
      const row = this.scheduleFFormView.rows[i];

      // ALWAYS save value1
      const formRowDataA = {
        tax_year: this.rowData.TAX_YEAR,
        trans_type_key: row.saveParams[1],
        line_order: row.line_order,
        attrib_order: row.attrib_order_a,
        line_description: row.description,
        combination_key: this.rowData.COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: row.saveParams[2],
        line_no: row.num,
        occurence: row.saveParams[7],
        line_attrib_key: row.lineAttrKeyA,
        Attribute_Name: row.attrib_name_a,
        Attribute_Value: row.value1,
        trans_dtls_key: row.saveParams[3],
        trans_status: row.saveParams[6],
        is_dirty: ''
      };
      tempArray.push(formRowDataA);

      // CRITICAL: Only save value2 if i > 1 (SuperPrompt V9 - PUŁAPKA #2)
      if (i > 1) {
        const formRowDataB = {
          tax_year: this.rowData.TAX_YEAR,
          trans_type_key: row.saveParams[1],
          line_order: row.line_order,
          attrib_order: row.attrib_order_b,
          line_description: row.description,
          combination_key: this.rowData.COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: row.saveParams[2],
          line_no: row.num,
          occurence: row.saveParams[7],
          line_attrib_key: row.lineAttrKeyB,
          Attribute_Name: row.attrib_name_b,
          Attribute_Value: row.value2,
          trans_dtls_key: row.saveParams[3],
          trans_status: row.saveParams[6],
          is_dirty: ''
        };
        tempArray.push(formRowDataB);
      }
    }

    const formData = {
      addrows: [],
      editrows: [tempArray],
      removerows: []
    };

    const mainData = [formData];

    // Save to backend
    this.f1065SchCService.saveScheduleCForm(
      this.GENERAL_CONFIG.base_url,
      mainData,
      this.globalService,
      this.filterParams,
      this.rowData,
      this.processName  // CRITICAL: Use dynamic process name from workspaceFactory (SuperPrompt V9)
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this._snackBar.open('Form has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // CRITICAL: Emit 'refresh' string (SuperPrompt V9)
          this.requestGridUpdate.emit('refresh');
        } else {
          this._snackBar.open(response.errorMessage || 'Error occurred while saving', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.scheduleFFormView = JSON.parse(JSON.stringify(this.formListOriginal));
    this.cdr.detectChanges();
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal (SuperPrompt V9)
    this.requestGridUpdate.emit('cancel');
  }
}

